import { useOrgModules } from "@/modules/useOrgModules";
import { useState, type ReactNode } from "react";
import {
  Calendar,
  ListTodo,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Phone,
  StickyNote,
} from "lucide-react";
import { useGetOne, type Identifier } from "ra-core";

import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Contact } from "@/components/atomic-crm/types";
import {
  ClientActivityComposer,
  type ClientActivityAction,
} from "@/modules/clients/ClientActivityComposer";
import { companyScope } from "@/modules/clients/clientScope";
import { normalizePhoneForTel } from "@/lib/linking";
import { cn } from "@/lib/utils";
import {
  getCompanyPrimaryContactEmail,
  getContactPrimaryEmail,
} from "@/modules/billing/billingRecipientResolution";
import {
  resolveCompanyEmailForDisplay,
  resolveCompanyPhoneRaw,
} from "@/modules/clients/companyChannelResolvers";
import { OpenMailComposeButton } from "@/modules/mail/OpenMailComposeButton";
import { useOpenCrmEmail } from "@/modules/mail/openCrmEmail";
import { useCrmPhoneCall } from "@/modules/voice/useCrmPhoneCall";
import { CrmPhoneLink } from "@/modules/voice/CrmPhoneLink";
import { contactHasSmsPhone } from "@/modules/messages/messageContactUtils";
import { useMessagingEnabled } from "@/modules/messages/useMessagingEnabled";
import { useMessagesQuickAccessOptional } from "@/modules/messages/messagesQuickAccessContext";
import {
  getPrimaryContactPhoneRaw,
  type CompanyWithPrimaryContact,
} from "@/modules/clients/clientProfile";

type ClientQuickActionsProps = {
  record: CompanyWithPrimaryContact;
  primaryContactId?: Identifier | null;
  /** Preview-style Call/Email chips + compact secondary actions. */
  presentation?: "circles" | "strip";
  className?: string;
};

const LabeledAction = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex min-w-0 flex-col items-center gap-1.5">
    {children}
    <span className="text-2xs text-muted-foreground">{label}</span>
  </div>
);

const CircleButton = ({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
}) => {
  const button = (
    <IconButton
      variant="secondary"
      className="size-10 shrink-0 rounded-full"
      disabled={disabled}
      aria-label={label}
      onClick={onClick}
    >
      {children}
    </IconButton>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};

export const ClientQuickActions = ({
  record,
  primaryContactId,
  presentation = "circles",
  className,
}: ClientQuickActionsProps) => {
  const { smsEnabled } = useMessagingEnabled();
  // Company modules (Apps): hide actions of modules that are off.
  const { isEnabled } = useOrgModules();
  const showMessaging = isEnabled("messages");
  const showMeeting = isEnabled("calendar");
  const messagesQuickAccess = useMessagesQuickAccessOptional();
  const openCrmEmail = useOpenCrmEmail();
  const [logAction, setLogAction] = useState<ClientActivityAction | null>(null);

  const { data: primaryContact } = useGetOne<Contact>(
    "contacts",
    { id: primaryContactId! },
    { enabled: !!primaryContactId },
  );

  // General contact, not billing: the primary contact, else the company inbox.
  const email =
    getContactPrimaryEmail(primaryContact) ||
    getCompanyPrimaryContactEmail(record) ||
    (() => {
      const display = resolveCompanyEmailForDisplay(record);
      return display !== "—" ? display : "";
    })();
  const phoneRaw =
    resolveCompanyPhoneRaw(record) || getPrimaryContactPhoneRaw(record) || "";
  const phoneLink = phoneRaw ? normalizePhoneForTel(phoneRaw) : null;
  const { canCall, voiceReady, callPhone } = useCrmPhoneCall();
  const canCallPhone = Boolean(phoneLink?.e164 && canCall && voiceReady);
  const canSms =
    smsEnabled &&
    primaryContact &&
    contactHasSmsPhone(primaryContact) &&
    messagesQuickAccess;

  if (presentation === "strip") {
    return (
      <TooltipProvider delayDuration={200}>
        <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
          {phoneRaw ? (
            <CrmPhoneLink
              phone={phoneRaw}
              contactId={primaryContactId ?? undefined}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground"
            >
              <Phone className="size-3.5" />
              Call
            </CrmPhoneLink>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-8 gap-1.5"
              disabled
            >
              <Phone className="size-3.5" />
              Call
            </Button>
          )}
          {email ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-8 gap-1.5"
              onClick={() => {
                openCrmEmail({
                  to: email,
                  companyId: record.id,
                  contactId: primaryContactId ?? undefined,
                });
              }}
            >
              <Mail className="size-3.5" />
              Email
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-8 gap-1.5"
              disabled
            >
              <Mail className="size-3.5" />
              Email
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-8 gap-1.5"
            disabled={!canSms}
            onClick={() => {
              if (primaryContact && messagesQuickAccess) {
                void messagesQuickAccess.openSms(primaryContact);
              }
            }}
          >
            <MessageSquare className="size-3.5" />
            Message
          </Button>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className={cn("flex items-start justify-end gap-2", className)}>
        <LabeledAction label="Note">
          <CircleButton label="Note" onClick={() => setLogAction("note")}>
            <StickyNote className="size-4" />
          </CircleButton>
        </LabeledAction>

        <LabeledAction label="Email">
          <OpenMailComposeButton
            to={email}
            companyId={record.id}
            contactId={primaryContactId ?? undefined}
          >
            <Mail className="size-4" />
          </OpenMailComposeButton>
        </LabeledAction>

        {showMessaging ? (
          <>
            <LabeledAction label="Call">
              <CircleButton
                label="Call"
                onClick={() => {
                  if (phoneLink?.e164) {
                    void callPhone({
                      to: phoneLink.e164,
                      contactId: primaryContactId ?? undefined,
                    });
                  }
                }}
                disabled={!canCallPhone}
              >
                <Phone className="size-4" />
              </CircleButton>
            </LabeledAction>

            <LabeledAction label="Message">
              <CircleButton
                label="Message"
                disabled={!canSms}
                onClick={() => {
                  if (primaryContact && messagesQuickAccess) {
                    void messagesQuickAccess.openSms(primaryContact);
                  }
                }}
              >
                <MessageSquare className="size-4" />
              </CircleButton>
            </LabeledAction>
          </>
        ) : null}

        <LabeledAction label="More">
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <IconButton
                    variant="secondary"
                    className="size-10 rounded-full"
                    aria-label="More actions"
                  >
                    <MoreHorizontal className="size-4" />
                  </IconButton>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>More</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLogAction("note")}>
                <StickyNote className="size-4" />
                Note
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLogAction("task")}>
                <ListTodo className="size-4" />
                Task
              </DropdownMenuItem>
              {showMeeting ? (
                <DropdownMenuItem onClick={() => setLogAction("meeting")}>
                  <Calendar className="size-4" />
                  Meeting
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </LabeledAction>
      </div>

      <Dialog
        open={logAction != null}
        onOpenChange={(open) => {
          if (!open) setLogAction(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Log activity</DialogTitle>
            <DialogDescription>
              Choose the contact this note, task or meeting belongs to.
            </DialogDescription>
          </DialogHeader>
          {logAction ? (
            <ClientActivityComposer
              key={logAction}
              scope={companyScope(record.id)}
              primaryContactId={primaryContactId}
              initialAction={logAction}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};
